import { Link, useLocation, useNavigate } from 'react-router-dom';
import React from 'react';
import { FaBell } from 'react-icons/fa';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { API_BASE } from '../utils/apiEndpoints';
import SiteLogo from './SiteLogo.tsx';
import './css/Navigation.css';

function Navigation() {
  const { t, currentLanguage, setLanguage, translations } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate(); // Добавляем useNavigate здесь
  const { isAuthenticated, user, logout, loading: authLoading } = useAuth();

  // Определяем активную страницу
  const isActive = (path: string) => location.pathname === path;
  const isSubscriptionActive =
    location.pathname === '/subscription' || location.pathname.startsWith('/subscription/');
  const isEducationActive =
    location.pathname === '/my-class' ||
    location.pathname === '/join-class' ||
    location.pathname.startsWith('/class/');

  const languagesToRender = ['ru', 'en'];
  const languageLabel: Record<string, string> = {
    ru: 'RU',
    en: 'EN',
  };

  const [langMenuOpen, setLangMenuOpen] = React.useState(false);
  const langMenuRef = React.useRef<HTMLDivElement>(null);
  const [unreadCount, setUnreadCount] = React.useState(0);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/notifications/unread-count`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) setUnreadCount(Number(data.count) || 0);
      } catch {
        if (!cancelled) setUnreadCount(0);
      }
    };
    void load();
    const timer = window.setInterval(load, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isAuthenticated, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login'); // Навигация при logout
  };

  const handleLanguageSelect = (lang: string) => {
    setLanguage(lang);
    setLangMenuOpen(false);
  };

  return (
    <nav className="sciplex-nav">
      <div className="nav-container">
        <div className="nav-logo">
          <SiteLogo />
        </div>

        <div className="nav-links-wrap">
          <div className="nav-links">
            <NavButton 
              to="/problems" 
            active={isActive('/problems')}
            text={t('navigation.problems')}
          />
          {isAuthenticated && (
            <NavButton
              to="/my-class"
              active={isEducationActive}
              text={t('navigation.myClass')}
            />
          )}
          <NavButton 
            to="/theory" 
            active={isActive('/theory')}
            text={t('navigation.theory')}
          />
          <NavButton 
            to="/leaderboard" 
            active={isActive('/leaderboard')}
            text={t('navigation.leaderboard')}
            />
          <NavButton 
            to="/subscription" 
            active={isSubscriptionActive}
            text={t('navigation.subscription')}
            />
          </div>
        </div>
        
        <div className="nav-actions">
          {authLoading ? null : isAuthenticated ? (
            <>
              <Link to="/subscription" className="nav-premium">
                Premium
              </Link>
              <Link
                to="/notifications"
                className="nav-bell"
                aria-label={t('navigation.notifications')}
                title={t('navigation.notifications')}
              >
                <FaBell aria-hidden />
                {unreadCount > 0 ? (
                  <span className="nav-bell__count">{unreadCount > 99 ? '99+' : unreadCount}</span>
                ) : null}
              </Link>
              <div className="nav-profile">
                <div className="profile-dropdown">
                  <button className="profile-button">
                    <span className="profile-username">{user?.username}</span>
                  </button>
                  <div className="dropdown-menu">
                    <Link to="/profile" className="dropdown-item">
                      {t('navigation.profile')}
                    </Link>
                    <Link to="/notifications" className="dropdown-item">
                      {t('navigation.notifications')}
                      {unreadCount > 0 ? ` (${unreadCount > 99 ? '99+' : unreadCount})` : ''}
                    </Link>
                    <Link to="/my-class" className="dropdown-item">
                      {t('navigation.myClass')}
                    </Link>
                    <Link to="/settings" className="dropdown-item">
                      {t('navigation.settings')}
                    </Link>
                    <Link to="/subscription" className="dropdown-item">
                      {t('navigation.subscription')}
                    </Link>
                    {user?.role === 'admin' && (
                      <Link to="/admin" className="dropdown-item">
                        {t('navigation.admin')}
                      </Link>
                    )}
                    {(user?.role === 'admin' || user?.role === 'hr') && (
                      <Link to="/hr" className="dropdown-item">
                        {t('navigation.hr')}
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="dropdown-item logout"
                    >
                      {t('navigation.logout')}
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <Link to="/subscription" className="nav-premium">
                Premium
              </Link>
              <Link to="/login" className="nav-login">
                {t('navigation.login')}
              </Link>
            </>
          )}

        </div>

        <div className="language-switcher" ref={langMenuRef}>
          <button
            type="button"
            className="language-select language-select-btn"
            onClick={() => setLangMenuOpen((v) => !v)}
            aria-expanded={langMenuOpen}
            aria-haspopup="listbox"
            aria-label="Выбор языка"
          >
            <span className="language-select-code">
              {languageLabel[currentLanguage] || currentLanguage.toUpperCase()}
            </span>
          </button>
          <div
            className={`language-dropdown ${langMenuOpen ? 'language-dropdown-open' : ''}`}
            role="listbox"
            aria-label="Языки"
          >
            {languagesToRender.map((lang) => (
              <button
                key={lang}
                type="button"
                role="option"
                aria-selected={currentLanguage === lang}
                className={`language-option ${currentLanguage === lang ? 'language-option-active' : ''}`}
                onClick={() => handleLanguageSelect(lang)}
              >
                <span>{languageLabel[lang] || lang}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </nav>
  )
};

// Отдельный компонент для кнопок навигации
function NavButton({ to, active, text }: { to: string; active: boolean; text: string }) {
  return (
    <div className={`nav-button ${active ? 'active' : ''}`}>
      <Link to={to}>{text}</Link>
    </div>
  );
}

export default Navigation;